import React, { useEffect, useState, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Gift, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, CalendarDays, Camera, Trophy, Check, X, RotateCcw, History } from 'lucide-react';
import { toast } from 'sonner';
import { giftsAPI, settingsAPI, uploadAPI, mediaUrl } from '../services/api';

const EMPTY = { name: '', description: '', required_points: 100, stock: '', valid_from: '', valid_until: '', conditions: '', photo_url: null, active: true };

const AWARD_LABEL = { attribue: 'Attribué', remis: 'Remis', annule: 'Annulé' };
const AWARD_COLOR = { attribue: 'bg-orange-100 text-orange-700 border-orange-200', remis: 'bg-green-100 text-green-700 border-green-200', annule: 'bg-slate-100 text-slate-500 border-slate-200' };

// -------- Challenge settings (start date + default promo) --------
const StartDateCard = () => {
  const [startDate, setStartDate] = useState('');
  const [initialDate, setInitialDate] = useState('');
  const [promo, setPromo] = useState('');
  const [bareme, setBareme] = useState({ simple: 10, photo: 15, video: 20 });
  const [invGoal, setInvGoal] = useState(5);
  const [presGoal, setPresGoal] = useState(3);
  const [distStart, setDistStart] = useState('');
  const [distEnd, setDistEnd] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsAPI.get().then((s) => {
      setStartDate(s.challenge_start_date || '');
      setInitialDate(s.challenge_start_date || '');
      setPromo(s.default_promo_code || '');
      setBareme(s.proof_points || { simple: 10, photo: 15, video: 20 });
      setInvGoal(s.distrib_invitations_goal ?? 5);
      setPresGoal(s.distrib_presentations_goal ?? 3);
      setDistStart(s.distrib_start_date || '');
      setDistEnd(s.distrib_end_date || '');
    }).catch(() => {});
  }, []);

  const save = async () => {
    const isNewCycle = startDate && startDate !== initialDate;
    if (isNewCycle && !window.confirm(
      "⚠️ Démarrer un NOUVEAU challenge à cette date va ARCHIVER les résultats actuels puis TOUT remettre à zéro (progression, points, badges, classement, cadeaux) pour tous les membres. Continuer ?"
    )) return;
    setSaving(true);
    try {
      const res = await settingsAPI.update({
        challenge_start_date: startDate || '',
        default_promo_code: promo.trim() || 'bfc594',
        proof_points: {
          simple: parseInt(bareme.simple, 10) || 0,
          photo: parseInt(bareme.photo, 10) || 0,
          video: parseInt(bareme.video, 10) || 0,
        },
        distrib_invitations_goal: parseInt(invGoal, 10) || 0,
        distrib_presentations_goal: parseInt(presGoal, 10) || 0,
        distrib_start_date: distStart || '',
        distrib_end_date: distEnd || '',
      });
      setInitialDate(startDate || '');
      if (res.cycle_reset) toast.success('Nouveau challenge démarré — tout a été remis à zéro et archivé');
      else toast.success('Réglages enregistrés');
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50" data-testid="challenge-start-card">
      <h3 className="font-bold text-red-950 dark:text-red-50 mb-1 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-red-600" />Réglages du challenge</h3>
      <p className="text-xs text-muted-foreground mb-3">Date de démarrage commune (vide = à l'inscription), code promo par défaut, et barème de points des preuves.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="start-date">Jour 1 le</Label>
          <Input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="challenge-start-input" className="rounded-xl w-44" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="default-promo">Code promo par défaut</Label>
          <Input id="default-promo" value={promo} onChange={(e) => setPromo(e.target.value)} data-testid="default-promo-input" placeholder="bfc594" className="rounded-xl w-40" />
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-xs">Barème de points (validation)</Label>
        <div className="grid grid-cols-3 gap-3 mt-1.5">
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Simple</Label><Input type="number" value={bareme.simple} onChange={(e) => setBareme((b) => ({ ...b, simple: e.target.value }))} data-testid="bareme-simple" className="rounded-xl" /></div>
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Avec photo</Label><Input type="number" value={bareme.photo} onChange={(e) => setBareme((b) => ({ ...b, photo: e.target.value }))} data-testid="bareme-photo" className="rounded-xl" /></div>
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Avec vidéo</Label><Input type="number" value={bareme.video} onChange={(e) => setBareme((b) => ({ ...b, video: e.target.value }))} data-testid="bareme-video" className="rounded-xl" /></div>
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-xs">Objectifs hebdomadaires distributeur</Label>
        <div className="grid grid-cols-2 gap-3 mt-1.5">
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Invitations / semaine</Label><Input type="number" min="0" value={invGoal} onChange={(e) => setInvGoal(e.target.value)} data-testid="goal-invitations-input" className="rounded-xl" /></div>
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Présentations / semaine</Label><Input type="number" min="0" value={presGoal} onChange={(e) => setPresGoal(e.target.value)} data-testid="goal-presentations-input" className="rounded-xl" /></div>
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-xs">Dates des défis distributeur</Label>
        <p className="text-[11px] text-muted-foreground mb-1.5">Affichées aux distributeurs sur la page « Défis à relever ».</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Date de démarrage</Label><Input type="date" value={distStart} onChange={(e) => setDistStart(e.target.value)} data-testid="distrib-start-date" className="rounded-xl" /></div>
          <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Date de fin</Label><Input type="date" value={distEnd} onChange={(e) => setDistEnd(e.target.value)} data-testid="distrib-end-date" className="rounded-xl" /></div>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <Button onClick={save} disabled={saving} data-testid="challenge-start-save" className="bg-orange-500 hover:bg-orange-600 rounded-full font-semibold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
        </Button>
        {startDate && (
          <Button variant="ghost" onClick={() => setStartDate('')} data-testid="challenge-start-clear" className="text-muted-foreground">Effacer la date</Button>
        )}
      </div>
    </Card>
  );
};

// -------- Gift form dialog --------
const GiftDialog = ({ open, onOpenChange, gift, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) setForm(gift ? { ...EMPTY, ...gift, stock: gift.stock ?? '', valid_from: gift.valid_from || '', valid_until: gift.valid_until || '' } : EMPTY);
  }, [open, gift]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      setForm((f) => ({ ...f, photo_url: res.url }));
    } catch { toast.error("Échec de l'envoi de la photo"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Le nom du cadeau est requis'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description || '',
      photo_url: form.photo_url || null,
      required_points: parseInt(form.required_points, 10) || 0,
      stock: form.stock === '' || form.stock === null ? null : parseInt(form.stock, 10),
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
      conditions: form.conditions || '',
      active: !!form.active,
    };
    try {
      if (gift) await giftsAPI.update(gift.gift_id, payload);
      else await giftsAPI.create(payload);
      toast.success(gift ? 'Cadeau mis à jour' : 'Cadeau créé');
      onOpenChange(false);
      onSaved();
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="gift-dialog">
        <DialogHeader><DialogTitle>{gift ? 'Modifier le cadeau' : 'Nouveau cadeau'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-20 h-20 rounded-xl overflow-hidden bg-muted flex items-center justify-center shrink-0">
              {form.photo_url ? <img src={mediaUrl(form.photo_url)} alt="" className="w-full h-full object-cover" /> : <Gift className="w-8 h-8 text-muted-foreground" />}
            </div>
            <div>
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="gift-photo-upload" className="rounded-full">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-1" />Photo</>}
              </Button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="gift-photo-input" />
            </div>
          </div>
          <div className="space-y-1.5"><Label>Nom</Label><Input value={form.name} onChange={set('name')} data-testid="gift-name" placeholder="Ex : Gourde isotherme BFC21" className="rounded-xl" /></div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={set('description')} data-testid="gift-description" rows={2} className="rounded-xl resize-none" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Points requis</Label><Input type="number" value={form.required_points} onChange={set('required_points')} data-testid="gift-points" className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>Stock (vide = illimité)</Label><Input type="number" value={form.stock} onChange={set('stock')} data-testid="gift-stock" placeholder="∞" className="rounded-xl" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Valide du</Label><Input type="date" value={form.valid_from} onChange={set('valid_from')} data-testid="gift-valid-from" className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>au</Label><Input type="date" value={form.valid_until} onChange={set('valid_until')} data-testid="gift-valid-until" className="rounded-xl" /></div>
          </div>
          <div className="space-y-1.5"><Label>Conditions d'éligibilité</Label><Input value={form.conditions} onChange={set('conditions')} data-testid="gift-conditions" placeholder="Ex : réservé au Top 10" className="rounded-xl" /></div>
          <div className="flex items-center justify-between pt-1">
            <Label>Cadeau actif (visible par les membres)</Label>
            <Switch checked={!!form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="gift-active" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="gift-save" className="bg-orange-500 hover:bg-orange-600 rounded-full font-semibold w-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CoachGifts = () => {
  const [gifts, setGifts] = useState([]);
  const [eligibility, setEligibility] = useState([]);
  const [awards, setAwards] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([giftsAPI.manage(), giftsAPI.eligibility(), giftsAPI.awards()])
      .then(([g, e, a]) => { setGifts(g); setEligibility(e); setAwards(a); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (g) => { setEditing(g); setDialogOpen(true); };

  const remove = async (g) => {
    if (!window.confirm(`Supprimer « ${g.name} » ?`)) return;
    await giftsAPI.remove(g.gift_id);
    toast.success('Cadeau supprimé');
    load();
  };

  const move = async (idx, dir) => {
    const arr = [...gifts];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setGifts(arr);
    await giftsAPI.reorder(arr.map((g) => g.gift_id));
  };

  const toggleActive = async (g) => {
    await giftsAPI.update(g.gift_id, { active: !g.active });
    load();
  };

  const attribute = async (giftId, userId) => {
    await giftsAPI.award({ gift_id: giftId, user_id: userId });
    toast.success('Cadeau attribué');
    load();
  };

  const setAwardStatus = async (awardId, status) => {
    await giftsAPI.updateAward(awardId, { status });
    toast.success('Statut mis à jour');
    load();
  };

  if (loading) return <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50"><Loader2 className="w-5 h-5 animate-spin text-red-600 mx-auto" /></Card>;

  return (
    <div className="space-y-4" data-testid="coach-gifts">
      <StartDateCard />

      {/* Gifts list */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-red-950 dark:text-red-50 flex items-center gap-2"><Gift className="w-5 h-5 text-red-600" />Cadeaux & paliers</h3>
          <Button size="sm" onClick={openNew} data-testid="gift-add" className="bg-orange-500 hover:bg-orange-600 rounded-full font-semibold"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
        </div>
        {gifts.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Aucun cadeau. Ajoute le premier palier de récompense !</p>
        ) : (
          <div className="space-y-2">
            {gifts.map((g, idx) => (
              <div key={g.gift_id} className="flex items-center gap-3 p-3 rounded-xl border border-border" data-testid={`gift-row-${g.gift_id}`}>
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex items-center justify-center shrink-0">
                  {g.photo_url ? <img src={mediaUrl(g.photo_url)} alt="" className="w-full h-full object-cover" /> : <Gift className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm text-foreground truncate">{g.name}</p>
                    {!g.active && <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[10px]">Inactif</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">{g.required_points} pts{g.stock != null ? ` · stock ${g.stock}` : ' · stock illimité'}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`gift-up-${g.gift_id}`} className="p-1.5 text-muted-foreground hover:text-red-600 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === gifts.length - 1} data-testid={`gift-down-${g.gift_id}`} className="p-1.5 text-muted-foreground hover:text-red-600 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                  <button onClick={() => toggleActive(g)} data-testid={`gift-toggle-${g.gift_id}`} className="p-1.5 text-muted-foreground hover:text-red-600">{g.active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}</button>
                  <button onClick={() => openEdit(g)} data-testid={`gift-edit-${g.gift_id}`} className="p-1.5 text-muted-foreground hover:text-red-600"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(g)} data-testid={`gift-delete-${g.gift_id}`} className="p-1.5 text-muted-foreground hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Eligibility (Top 10) */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <h3 className="font-bold text-red-950 dark:text-red-50 mb-1 flex items-center gap-2"><Trophy className="w-5 h-5 text-orange-500" />Éligibilité Top 10 & attribution</h3>
        <p className="text-xs text-muted-foreground mb-4">Seuls les membres du Top 10 ayant atteint le palier sont éligibles. Valide manuellement chaque attribution.</p>
        {eligibility.filter((g) => g.eligible_members.length > 0).length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-3">Aucun membre éligible pour le moment.</p>
        ) : (
          <div className="space-y-4">
            {eligibility.filter((g) => g.eligible_members.length > 0).map((g) => (
              <div key={g.gift_id} data-testid={`eligibility-${g.gift_id}`}>
                <p className="text-sm font-semibold text-foreground mb-2">{g.name} <span className="text-muted-foreground font-normal">· {g.required_points} pts</span></p>
                <div className="space-y-2">
                  {g.eligible_members.map((m) => (
                    <div key={m.user_id} className="flex items-center gap-3 p-2.5 rounded-xl bg-red-50/60 dark:bg-red-950/30">
                      <span className="w-6 text-center font-extrabold text-muted-foreground text-sm">{m.rank}</span>
                      <Avatar className="w-8 h-8"><AvatarImage src={mediaUrl(m.avatar)} /><AvatarFallback className="bg-red-600 text-white text-xs">{m.name?.charAt(0)}</AvatarFallback></Avatar>
                      <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-foreground truncate">{m.name}</p><p className="text-xs text-muted-foreground">{m.total_points} pts</p></div>
                      {m.award_status && m.award_status !== 'annule' ? (
                        <Badge className={`text-[10px] ${AWARD_COLOR[m.award_status]}`}>{AWARD_LABEL[m.award_status]}</Badge>
                      ) : (
                        <Button size="sm" onClick={() => attribute(g.gift_id, m.user_id)} data-testid={`attribute-${g.gift_id}-${m.user_id}`} className="bg-orange-500 hover:bg-orange-600 rounded-full text-xs h-8">Attribuer</Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Awards history */}
      {awards.length > 0 && (
        <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
          <h3 className="font-bold text-red-950 dark:text-red-50 mb-3 flex items-center gap-2"><History className="w-5 h-5 text-red-600" />Historique des attributions</h3>
          <div className="space-y-2">
            {awards.map((a) => (
              <div key={a.award_id} className="flex items-center gap-3 p-2.5 rounded-xl border border-border" data-testid={`award-row-${a.award_id}`}>
                <Avatar className="w-8 h-8"><AvatarImage src={mediaUrl(a.user_avatar)} /><AvatarFallback className="bg-red-600 text-white text-xs">{a.user_name?.charAt(0)}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{a.user_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{a.gift_name}</p>
                </div>
                <Badge className={`text-[10px] ${AWARD_COLOR[a.status]}`}>{AWARD_LABEL[a.status]}</Badge>
                <div className="flex items-center gap-1 shrink-0">
                  {a.status !== 'remis' && <button onClick={() => setAwardStatus(a.award_id, 'remis')} title="Marquer remis" data-testid={`award-remis-${a.award_id}`} className="p-1.5 text-muted-foreground hover:text-green-600"><Check className="w-4 h-4" /></button>}
                  {a.status !== 'annule' && <button onClick={() => setAwardStatus(a.award_id, 'annule')} title="Annuler" data-testid={`award-annule-${a.award_id}`} className="p-1.5 text-muted-foreground hover:text-red-600"><X className="w-4 h-4" /></button>}
                  {a.status === 'annule' && <button onClick={() => setAwardStatus(a.award_id, 'attribue')} title="Rétablir" data-testid={`award-restore-${a.award_id}`} className="p-1.5 text-muted-foreground hover:text-orange-600"><RotateCcw className="w-4 h-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <GiftDialog open={dialogOpen} onOpenChange={setDialogOpen} gift={editing} onSaved={load} />
    </div>
  );
};
